import React from 'react';

const faqs: { q: string; a: string }[] = [
  {
    q: 'How often does MFlow sync with Jira?',
    a: 'MFlow syncs automatically every hour. The first sync after connecting imports all data within your selected history window (full sync). Every subsequent sync is incremental — only changed issues and changelogs are fetched, so it is fast. You can also trigger a manual sync at any time from the Integrations page.',
  },
  {
    q: 'Can I connect multiple Jira workspaces?',
    a: 'Currently MFlow supports one Jira workspace per account. Multi-workspace support is planned for a future release.',
  },
  {
    q: 'Does MFlow modify my Jira data?',
    a: 'By default, MFlow only reads from Jira — it never modifies anything during a sync. However, the AI agent can create issues, edit fields, change statuses, and manage sprints if you explicitly ask it to. No changes are made to your workspace without a direct instruction from you in the chat.',
  },
  {
    q: 'Is my data shared with other users?',
    a: "Not unless you share it. Your Jira/Trello data, sync history, and AI chat sessions are only visible to you. The one exception is a Standalone project you invite someone to from Board settings → Members: once they accept, they see and work on that project's board with you.",
  },
  {
    q: 'Will the Coding Agent push to my PR by itself when a reviewer comments?',
    a: 'No. MFlow tracks review feedback on the PRs the agent opened, but resuming is always your choice: click "Resume — address feedback" on the task page. The resumed task pushes to the same branch and PR.',
  },
  {
    q: "Why wasn't my PR reviewed again by the Git Reviewer?",
    a: 'The Git Reviewer reviews each PR once per commit. It reviews again only after new commits are pushed. A manual run shows exactly why each PR was skipped.',
  },
  {
    q: 'What happens when I disconnect an integration?',
    a: 'MFlow immediately revokes the access token (or, for Email, forgets the verified address) and removes all data associated with that integration from its servers. Your Jira/Trello/Slack/Telegram/Email account itself is not affected.',
  },
  {
    q: 'Can I chat without picking a project first?',
    a: 'Yes. Go to Chat in the sidebar and click New chat — it opens a general conversation with no project attached. Attach a project to it at any point from the chat header, or just keep asking general questions.',
  },
  {
    q: 'Does the AI remember what I said in a different chat?',
    a: 'No — every session is isolated, even two sessions on the same project. Resume the same session to continue a conversation; a new session always starts with a blank history. See Chat Sessions & Context for the full explanation.',
  },
  {
    q: 'Which channel does MFlow actually notify me on if I have several connected?',
    a: 'Whichever one is enabled and has the highest priority in Settings — not all of them. If that send fails, MFlow falls back to the next enabled, connected channel in priority order. Set the order in Settings.',
  },
  {
    q: 'Can I use MFlow from Claude Code, Claude Desktop, or claude.ai?',
    a: 'Yes, via the MCP Server integration — connect it once from the Integrations page and ask about your projects, create tasks, or check delivery risk directly from that client, no API key needed.',
  },
  {
    q: 'Why are my PSI scores not updating?',
    a: 'For Jira, PSI is recomputed after each sync, and syncs run automatically every hour — go to Integrations and click Sync now for fresh data immediately. Standalone has no background sync at all; its PSI is calculated live every time you open the project, so there is nothing to wait on there. Trello boards do not have a PSI score.',
  },
  {
    q: 'My Telegram link expired. What do I do?',
    a: 'Click Cancel in the waiting state, then click Connect Telegram again. A new one-time link is generated, valid for another 10 minutes.',
  },
  {
    q: 'Can I use MFlow on mobile?',
    a: 'Yes. The web app is fully responsive and works on mobile browsers. The Telegram integration is also optimised for mobile use.',
  },
  {
    q: 'Does MFlow support light mode?',
    a: 'Yes. Toggle it from the theme button in the sidebar footer — it remembers your choice, and defaults to your OS preference the first time you visit.',
  },
  {
    q: 'Do I have to connect Jira or Trello to use MFlow?',
    a: "No. MFlow's own Standalone board needs no external account — create a project from the Standalone page or describe it to the Wizard. You can always transfer it to Jira or Trello later if you switch tools.",
  },
  {
    q: 'Which sandbox tier should I pick for a Coding Agent task?',
    a: "Light for landing pages, simple scripts, or documentation tweaks; Mid (the default) for typical projects; Heavy for large monorepos or builds that need real CPU/memory headroom, like a production Webpack/Vite/Next.js build. Picking a tier that's too small for a heavy build risks running the sandbox out of memory partway through.",
  },
];

const Faq: React.FC = () => (
  <div>
    <h2 className="text-2xl font-bold mb-6">FAQ</h2>
    <div className="space-y-2">
      {faqs.map(({ q, a }) => (
        <div key={q} className="collapse collapse-arrow bg-base-200 rounded-lg">
          <input type="checkbox" />
          <div className="collapse-title font-medium text-sm">{q}</div>
          <div className="collapse-content text-sm text-base-content/70 leading-relaxed">
            <p>{a}</p>
          </div>
        </div>
      ))}
    </div>
  </div>
);

export default Faq;
